<template>
  <div>
    <PageAllow controller="Organizations" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense>
        <v-col cols="12" sm="12" md="12" align-self="center">
          <v-row dense align="center">
            <v-spacer />
            <v-btn
              v-if="$permission.permissionAllow('Organizations', 'add')"
              small
              color="primary"
              @click.stop="
                currentItem = { id: 0 };
                editDialog = true;
              "
              class="my-3 mr-5"
            >
              <v-icon x-small class="pe-1">fa-plus</v-icon>
              {{ $t("CRUD.newInfo", [$t("Organization.ObjectName")]) }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-card class="mx-4 mb-4">
        <v-progress-linear :indeterminate="loading"></v-progress-linear>
        <v-btn text x-large color="primary" @click.stop="openAll = !openAll">
          {{ $t("openAll") }} / {{ $t("closeAll") }}
        </v-btn>
        <v-divider />
        <v-treeview
          class="py-2"
          v-model="tree"
          :items="organizations"
          item-key="code"
          item-children="organizations"
          :open="organizationsOpen"
          hoverable
          activatable
          return-object
        >
          <template v-slot:label="{ item }">
            <v-chip> {{ item.number }} </v-chip>
            {{ item.name }}

            <v-icon
              small
              v-if="$permission.permissionAllow('Organizations', 'edit')"
              color="primary"
              class="px-1 py-4 me-1"
              @click.stop="
                currentItem = JSON.parse(JSON.stringify(item));
                editDialog = true;
              "
              >fa-pencil-alt
            </v-icon>
            <v-icon
              small
              v-if="$permission.permissionAllow('Organizations', 'add')"
              color="primary"
              class="px-1 py-4 me-1"
              @click.stop="
                currentItem = {
                  parent_id: item.id,
                  parent_number: item.number,
                  parent_name: item.name,
                };
                editDialog = true;
              "
              >fa-plus
            </v-icon>
            <v-icon
              small
              v-if="
                item.deletable &&
                $permission.permissionAllow('Organizations', 'delete')
              "
              color="accent"
              class="px-1 py-4 me-1"
              @click.stop="
                currentItem = JSON.parse(JSON.stringify(item));
                deleteDialog = true;
              "
              >fa-trash-alt
            </v-icon>
          </template>
        </v-treeview>
      </v-card>
    </v-sheet>

    <v-dialog persistent v-model="editDialog" width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{
            $t("CRUD.info", [$t("Organization.ObjectName")])
          }}</span>

          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="editDialog = false">
                fa-times
              </v-icon>
            </template>
            <span>
              {{ $t("CRUD.backToList", [$t("Organization.ObjectName")]) }}
            </span>
          </v-tooltip>
        </v-card-title>
        <v-card-text>
          <v-container>
            <ValidationObserver ref="observer">
              <v-form>
                <v-row>
                  <v-col
                    cols="12"
                    v-if="currentItem.parent_id && !currentItem.id"
                  >
                    <v-text-field
                      required
                      readonly
                      :label="$t('Organization.Fields.parent_number_and_name')"
                      :value="
                        currentItem.parent_number +
                        ' / ' +
                        currentItem.parent_name
                      "
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('Organization.Fields.organization_number')"
                      rules="required"
                    >
                      <v-text-field
                        required
                        v-model="currentItem.number"
                        :error-messages="
                          $resparse
                            .getErrorMsgs(serverErrors, 'number')
                            .concat(errors)
                        "
                      >
                        <template v-slot:label>
                          {{ $t("Organization.Fields.organization_number") }}
                          <span class="red--text">*</span>
                        </template>
                      </v-text-field>
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('Organization.Fields.organization_name')"
                      rules="required"
                    >
                      <v-text-field
                        required
                        v-model="currentItem.name"
                        :error-messages="
                          $resparse
                            .getErrorMsgs(serverErrors, 'name')
                            .concat(errors)
                        "
                      >
                        <template v-slot:label>
                          {{ $t("Organization.Fields.organization_name") }}
                          <span class="red--text">*</span>
                        </template>
                      </v-text-field>
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-form>
            </ValidationObserver>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="editDialog = false">{{
            $t("$vuetify.close")
          }}</v-btn>
          <v-btn color="accent" @click="save()">{{ $t("CRUD.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <DialogDelete
      v-model="deleteDialog"
      :label="$t('Organization.ObjectName')"
      @confirm="
        del();
        deleteDialog = false;
      "
    />
  </div>
</template>

<script>
import i18n from "@/i18n";
import { mapState } from "vuex";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import DialogDelete from "@/components/plugins/DialogDelete";
import Organizations from "@/api/organizations";
import {
  ValidationObserver,
  ValidationProvider,
  setInteractionMode,
} from "vee-validate";

setInteractionMode("eager");

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    ValidationProvider,
    ValidationObserver,
    DialogDelete,
  },

  computed: {
    items() {
      return [
        {
          text: i18n.t("SettingNav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("SettingNav.settings"),
          disabled: false,
          href: "/settings/bussiness-settings",
        },
        {
          text: i18n.t("SettingNav.organization"),
          disabled: true,
          href: "/settings/organizations",
        },
      ];
    },
    // organizationsFilter() {
    //   return (item, search) =>
    //     item.name.indexOf(search) > -1 || item.number.indexOf(search) > -1;
    // },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading"]),
  },

  data: () => ({
    editDialog: false,
    deleteDialog: false,
    tree: [],
    organizations: [],
    currentItem: {},
    serverErrors: [],
    openAll: false,
    organizationsOpen: [],
  }),

  mounted() {
    this.getOrganizationsFromAPI();
  },

  methods: {
    openTree(openArr) {
      for (var i = 0; i < openArr.length; i++) {
        this.organizationsOpen.push(openArr[i]);
        if (openArr[i].organizations.length > 0) {
          this.openTree(openArr[i].organizations);
        }
      }
    },
    getOrganizationsFromAPI() {
      Organizations.tree().then(
        function (response) {
          if (response.data.status.isOK()) {
            this.organizations = response.data.organizations;
          }
        }.bind(this)
      );
    },

    save() {
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            Organizations.save(this.currentItem.id, this.currentItem)
              .then(
                function (response) {
                  if (response.data.status.isOK()) {
                    this.$emit("save", this.currentItem);
                    this.getOrganizationsFromAPI();
                    this.editDialog = false;
                    this.$store.dispatch("fullscreenLoadingStop");
                  } else if (
                    response.data.error &&
                    response.data.error.data &&
                    "ERR333" == response.data.error.code
                  ) {
                    this.serverErrors = response.data.error.data;
                  }
                }.bind(this)
              )
              .catch(
                function () {
                  this.editDialog = false;
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              );
          }
        }.bind(this)
      );
    },

    del() {
      Organizations.del(this.currentItem.id).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.getOrganizationsFromAPI();
          }
        }.bind(this)
      );
    },
  },

  watch: {
    editDialog: {
      handler() {
        if (!this.editDialog) {
          this.$refs.observer.reset();
          this.serverErrors = [];
          this.$store.dispatch("fullscreenLoadingStop");
        }
      },
    },
    openAll: {
      handler() {
        if (this.openAll) {
          this.openTree(this.organizations);
        } else {
          this.organizationsOpen = [];
        }
      },
    },
  },
};
</script>